import { trackById, trackByIndex } from 'src/app/shared/utils/track-by.util';
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, Subscription } from 'rxjs';
import { FileTypeEnum } from 'src/app/enums/fileType.enum';
import { SurveyDialogComponent } from 'src/app/modals/survey-dialog/survey-dialog.component';
import { FileAttachment } from 'src/app/models/fileAttachment.model';
import { Lookup } from 'src/app/models/lookup.model';
import { Sample } from 'src/app/models/sample.model';
import { SampleSurvey } from 'src/app/models/sampleSurvey.model';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';
import { ReportGenerationService } from 'src/app/services/report-generation.service';
import { SampleSurveyService } from 'src/app/services/sample-survey.service';
import { SampleService } from 'src/app/services/sample.service';
import { ToastService } from 'src/app/services/toast.service';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { GridPermissions, gridToPermissionFlags } from 'src/app/shared/grid/core-grid/gridPermissions.enum';
import { PermissionService } from 'src/app/services/permission.service';
import { PdfGeneratedData } from 'src/app/models/pdfGeneratedData.model';
import { DocumentType } from 'src/app/enums/documentType.enum';
import { EmailStatus } from 'src/app/enums/emailStatus.enum';
import { SurveyStatus, SurveyStatusLabels, SurveyStatusCssClasses } from 'src/app/enums/surveyStatus.enum';
import { ColumnDetails } from 'src/app/models/columnDetails.model';

const MAX_SAMPLES_PER_REPORT = 50;
const SAMPLE_SORT_FIELD = nameOf<Sample>(x => x.id);
const SAMPLE_SORT_DIRECTION = 'asc';
const FIRST_PAGE_INDEX = 0;

interface KeyValue { label: string; value: string | number; isClickable?: boolean }

@Component({
  standalone: false,
  selector: 'app-sample-survey-details',
  templateUrl: './sample-survey-details.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './sample-survey-details.component.scss'
})
export class SampleSurveyDetailsComponent implements OnInit, OnDestroy {
  sampleSurveyDetails: SampleSurvey;
  organizationsList: Lookup[] = [];
  SampleSurveyFields: KeyValue[] = [];
  attachedFiles: FileAttachment[] = [];
  attachedReports: FileAttachment[] = [];
  surveyStatusOptions = Object.values(SurveyStatus)
    .filter(v => typeof v === 'number')
    .map(value => ({
      value: value as SurveyStatus,
      labelKey: SurveyStatusLabels[value as SurveyStatus],
      cssClass: SurveyStatusCssClasses[value as SurveyStatus]
    }));
  isGeneratingReport = false;
  showReportGenerationPopup = false;
  samples: Sample[] = [];
  columnDetails: Record<string, ColumnDetails> = {};
  isLoading = false;
  private samplesSub?: Subscription;
  emailStatusEnum = EmailStatus;
  persistedSurveyStatus: SurveyStatus | null = null;
  canEdit = true;
  readonly permissionObject = RolePermissionObjects.Survey;
  readonly editAction = GridPermissions.Update;

  constructor(
    private organizationLevelService: OrganizationLevelService,
    private sampleSurveyService: SampleSurveyService,
    private toastService: ToastService,
    private activatedRoute: ActivatedRoute,
    protected dialog: MatDialog,
    private dateFormatService: DateFormatService,
    private router: Router,
    private sampleService: SampleService,
    private reportGenerationService: ReportGenerationService,
    private translateService: TranslateService,
    private permissionService: PermissionService
  ) { }

  ngOnInit(): void {
    const surveyId = this.activatedRoute.snapshot.params.id;
    this.getSampleSurveyDetails(surveyId);
  }

  ngOnDestroy(): void {
    this.samplesSub?.unsubscribe();
  }

  getSampleSurveyDetails(sampleId: string) {
    forkJoin({
      organizationsList: this.organizationLevelService.getAll(),
      sampleSurveyDetails: this.sampleSurveyService.getById(sampleId),
      columnDetails: this.sampleService.getFieldConfiguration()
    }).subscribe({

      next: ({ organizationsList, sampleSurveyDetails, columnDetails }) => {
        this.organizationsList = organizationsList;
        this.sampleSurveyDetails = sampleSurveyDetails;
        this.columnDetails = columnDetails;
        this.persistedSurveyStatus = sampleSurveyDetails.surveyStatus;
        this.updateCanEditStatus();
        this.populateSampleSurveyDetailsFields(sampleSurveyDetails);
      }, error: this.handleError
    });
  }

  populateSampleSurveyDetailsFields(data: SampleSurvey) {
    this.SampleSurveyFields = [
      { label: this.translateService.instant('Survey Id'), value: data.id },
      { label: this.translateService.instant('Survey Name'), value: data.name },
      { label: this.translateService.instant('Survey Code'), value: data.surveyCode },
      { label: this.translateService.instant('Survey Start Date'), value: this.dateFormatService.convertUTCDateStringToUserTimeZoneString(data.surveyPlannedStartDate, this.dateFormatService.userDateFormat) },
      { label: this.translateService.instant('Survey End Date'), value: this.dateFormatService.convertUTCDateStringToUserTimeZoneString(data.surveyPlannedEndDate, this.dateFormatService.userDateFormat) },
      { label: this.translateService.instant('Survey Description'), value: data.surveyDescription },
      { label: this.translateService.instant('Survey Type'), value: data.surveyTypeName },
      { label: this.translateService.instant('No. of Samples Taken'), value: data.numberOfSamplesTaken, isClickable: data.numberOfSamplesTaken > 0 },
      { label: this.translateService.instant('Org Level'), value: data.organizationLevelName },
      { label: this.translateService.instant('Requested By'), value: data.requestedByName },
    ]
    data.fileAttachments = data.fileAttachments.map(file => ({ ...file, imgSrc: this.getImgSrc(file?.fileType) }))
    this.attachedFiles = data.fileAttachments.filter(file => ![DocumentType.Letters, DocumentType.Report].includes(file.documentType));
    this.attachedReports = data.fileAttachments.filter(file => file.documentType === DocumentType.Report);
  }

  getImgSrc(fileType: FileTypeEnum): string {
    switch (fileType) {
      case FileTypeEnum.Pdf: return 'assets/images/file-note.svg';
      case FileTypeEnum.Image: return 'assets/images/image.svg';
      default: return 'assets/images/file-note.svg'
    }
  }

  downloadAttachment(attachment: any) {
    if (attachment.url) {
      const a = document.createElement('a');
      a.href = attachment.url;
      a.download = attachment.name || 'download';
      a.target = '_blank';
      a.click();
    } else {
      this.toastService.displayErrorMessage('No URL available for download', true)
    }
  }

  onSurveyClosedChange(): void {
    const previousStatus = this.persistedSurveyStatus;
    this.sampleSurveyService.update(this.sampleSurveyDetails).subscribe({
      next: () => {
        this.persistedSurveyStatus = this.sampleSurveyDetails.surveyStatus;
        this.updateCanEditStatus();
        this.toastService.showSuccess(this.translateService.instant('Survey status updated successfully'), true);
      },
      error: (error) => {
        if (previousStatus != null) {
          this.sampleSurveyDetails.surveyStatus = previousStatus;
        }
        this.handleError(error);
      }
    });
  }

  private handleError = (error: any): void => {
    this.toastService.showError(error);
  }

  onClick(field) {
    if (field.label === this.translateService.instant('No. of Samples Taken')) {
      this.openSampleGrid(this.sampleSurveyDetails);
    }
  }

  openSampleGrid(item: SampleSurvey) {
    this.router.navigate([`/dashboard/samples/sample`],
      { queryParams: { additionalFilters: JSON.stringify({ sampleSurveyId: item.id.toString() }) } }
    );
  }


  openSampleSurveyDialog() {
    const dialogRef = this.dialog.open(SurveyDialogComponent, {
      data: {
        id: this.sampleSurveyDetails.id
      },
      width: '920px',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) { this.getSampleSurveyDetails(result.id ?? this.sampleSurveyDetails.id); }
    });
  }

  openReportGenerationPopup() {
    if (!this.sampleSurveyDetails?.id) {
      this.toastService.displayErrorMessage('Sample survey data not loaded', true);
      return;
    }

    this.isGeneratingReport = true;
    this.samples = [];
    this.showReportGenerationPopup = true;

    const filterBy = {
      sampleSurveyId: this.sampleSurveyDetails.id.toString()
    };

    this.samplesSub?.unsubscribe();
    this.samplesSub = this.sampleService
      .get('', SAMPLE_SORT_FIELD, SAMPLE_SORT_DIRECTION, FIRST_PAGE_INDEX, MAX_SAMPLES_PER_REPORT, JSON.stringify(filterBy))
      .subscribe({
      next: (response) => {
        this.isGeneratingReport = false;
        if (response.data && response.data.length > 0) {
          this.samples = response.data;
        } else {
          this.showReportGenerationPopup = false;
          this.toastService.showError(this.translateService.instant('No samples found for this survey'));
        }
      },
      error: (error) => {
        this.isGeneratingReport = false;
        this.showReportGenerationPopup = false;
        this.toastService.showError(this.translateService.instant('Failed to load samples for the survey.'));
      }
    });
  }

  closeReportGenerationPopup() {
    this.samplesSub?.unsubscribe();
    this.samplesSub = undefined;
    this.isGeneratingReport = false;
    this.showReportGenerationPopup = false;
  }

  async onPdfGenerated(event: PdfGeneratedData) {
    const organizationName = this.organizationsList.find(org => org.id === this.sampleSurveyDetails.organizationLevelId)?.name || '';

    await this.reportGenerationService.generateAndSaveReport(event, {
      entityIds: [event.entityId],
      entityType: event.entityType,
      organizationLevelId: this.sampleSurveyDetails.organizationLevelId,
      organizationLevelName: organizationName,
      metadataKey: RolePermissionObjects.Survey
    }, true, false);
  }

  get canChangeStatus(): boolean {
    return this.permissionService.hasPermissionFlag(
      this.permissionObject,
      gridToPermissionFlags[GridPermissions.Update]
    );
  }

  get editTooltip(): string {
    return this.canEdit
      ? this.translateService.instant('Edit')
      : this.translateService.instant('Survey is locked and cannot be edited');
  }

  private updateCanEditStatus(): void {
    if (!this.persistedSurveyStatus && this.persistedSurveyStatus !== 0) {
      this.canEdit = true;
      return;
    }

    // Locked statuses that prevent editing
    const lockedStatuses = [
      SurveyStatus.Closed,
      SurveyStatus.ReportGenerated,
      SurveyStatus.Archived
    ];

    this.canEdit = !lockedStatuses.includes(this.persistedSurveyStatus as SurveyStatus);
  }

    public trackById = trackById;
    public trackByIndex = trackByIndex;
}
